import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Grid,
  Alert,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  CloudUpload as CloudUploadIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import axios from 'axios';
import acProcessService from '../../../services/acProcessService';
import FileUploadComponent from '../../../components/FileUploadComponent';
import s3Utils from "../../../utils/s3Utils";


const UploadMOM = () => {
  const navigate = useNavigate();

  // Form state
  const [formData, setFormData] = useState({
    acType: '0',
    fromDate: '0',
    toDate: '0',
    approvalDate: '',
  });

  // Data state
  const [acDates, setAcDates] = useState([]);
  const [acDatesTo, setAcDatesTo] = useState([]);
  const [momRecords, setMomRecords] = useState([]);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [password, setPassword] = useState('');
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingRecord, setEditingRecord] = useState(null);

  // File URLs for edit mode
  const [existingWordFile, setExistingWordFile] = useState('');
  const [existingPdfFile, setExistingPdfFile] = useState('');

  // File upload data from S3
  const [uploadedWordFile, setUploadedWordFile] = useState(null);
  const [uploadedPdfFile, setUploadedPdfFile] = useState(null);

  // Key to force FileUploadComponent remount on reset
  const [fileUploadKey, setFileUploadKey] = useState(0);

  useEffect(() => {
    loadMomRecords();
  }, []);

  const loadMomRecords = async () => {
    try {
      setLoading(true);
      const response = await acProcessService.getUploadedMOMFiles();
      const records = Array.isArray(response) ? response : (response.data || []);
      setMomRecords(records);
    } catch (err) {
      console.error('Failed to load MOM records:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleACTypeChange = async (value) => {
    setFormData(prev => ({ ...prev, acType: value, fromDate: '0', toDate: '0' }));
    setAcDatesTo([]);

    if (value !== '0') {
      try {
        setLoading(true);
        const response = await acProcessService.getRecentACDates(value);
        const dates = Array.isArray(response) ? response : (response.data || []);
        setAcDates(dates);
      } catch (err) {
        setError('Failed to load AC dates');
      } finally {
        setLoading(false);
      }
    }
  };

  const handleFromDateChange = async (value) => {
    setFormData(prev => ({ ...prev, fromDate: value, toDate: '0' }));

    if (value !== '0') {
      try {
        setLoading(true);
        const selectedDate = acDates.find(d => d.Id === value);
        const response = await acProcessService.getACDateToByDate(
          formData.acType,
          selectedDate?.Date
        );
        const datesTo = Array.isArray(response) ? response : (response.data || []);
        setAcDatesTo(datesTo);

        // Set min date for approval date
        if (selectedDate) {
          const dateStr = selectedDate.Date; // Assuming format dd/MM/yyyy
          const parts = dateStr.split('/');
          const minDate = `${parts[2]}-${parts[1]}-${parts[0]}`;
          setFormData(prev => ({ ...prev, approvalDate: '' }));
        }
      } catch (err) {
        setError('Failed to load end dates');
      } finally {
        setLoading(false);
      }
    }
  };

  // Handle file upload from FileUploadComponent
  const handleFileUploadChange = (fieldName, fileData) => {
    if (fieldName === 'wordFile') {
      if (fileData) {
        setUploadedWordFile(fileData);
        setSuccess('Word file uploaded successfully');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setUploadedWordFile(null);
      }
    } else if (fieldName === 'pdfFile') {
      if (fileData) {
        setUploadedPdfFile(fileData);
        setSuccess('PDF file uploaded successfully');
        setTimeout(() => setSuccess(''), 2000);
      } else {
        setUploadedPdfFile(null);
      }
    }
  };



  const validateForm = () => {
    if (formData.acType === '0') {
      setError('Please select AC Type');
      return false;
    }

    if (formData.fromDate === '0') {
      setError('Please select From Date');
      return false;
    }

    if (formData.toDate === '0') {
      setError('Please select To Date');
      return false;
    }

    if (!formData.approvalDate) {
      setError('Please select Approval Date');
      return false;
    }

    if (!isEditMode && !uploadedWordFile) {
      setError('Please select MOM file in Word format');
      return false;
    }

    if (!isEditMode && !uploadedPdfFile) {
      setError('Please select MOM file in PDF format');
      return false;
    }

    if (isEditMode && !uploadedWordFile && !existingWordFile) {
      setError('Please select MOM file in Word format');
      return false;
    }

    if (isEditMode && !uploadedPdfFile && !existingPdfFile) {
      setError('Please select MOM file in PDF format');
      return false;
    }

    return true;
  };

  const handleSubmitClick = () => {
    if (!validateForm()) {
      return;
    }
    setPasswordDialog(true);
  };

  const handleSubmit = async () => {
    if (!password.trim()) {
      setError('Please enter password');
      return;
    }

    try {
      setLoading(true);
      setError('');

      // First verify password
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/auth/verify-password`,
        {
          orgId: localStorage.getItem("orgId") || "",
          password: password
        }
      );

      if (!response.data.success) {
        setError('Invalid password. Please try again.');
        setLoading(false);
        setPasswordDialog(false);
        return;
      }

      // Check if MOM already exists for this date (only for new records)
      if (!isEditMode) {
        const selectedDate = acDates.find(d => d.Id === formData.fromDate);
        const selectedToDate = acDatesTo.find(d => (d.Id || d.id) === formData.toDate);
        const exists = await acProcessService.getMOMFilesFiltered(
          formData.acType,
          selectedDate?.Date,
          selectedToDate?.DateTo
        );
        if (exists && exists.data && exists.data.length > 0) {
          setError('MOM file already uploaded for this date');
          setLoading(false);
          setPasswordDialog(false);
          return;
        }
      }

      // Prepare upload data
      const uploadData = {
        acType: formData.acType,
        acDate: formData.fromDate,  // Send ID (Ac_Date)
        acDateTo: formData.toDate,  // Send ID (Ac_Date_To)
        approvalDate: formData.approvalDate,
        momFile: uploadedWordFile?.fileName || uploadedWordFile?.originalName || existingWordFile || '',
        momFormat: uploadedPdfFile?.fileName || uploadedPdfFile?.originalName || existingPdfFile || '',
        updatedBy: localStorage.getItem("orgId") || "",
      };

      const uploadResponse = await acProcessService.uploadMOMFile(uploadData);
      
      if (!isEditMode && uploadResponse && uploadResponse.status === 'success') {
        await acProcessService.updateStageTo6e(formData.fromDate);
      }
      
      setSuccess('MOM file uploaded successfully');
      setPasswordDialog(false);
      setPassword('');

      // Reset form
      resetForm();

      // Reload records
      await loadMomRecords();

      // Show success message and redirect after delay
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError('Failed to upload MOM file');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (record) => {
    console.log('Edit record:', record); // Debug log
    
    setIsEditMode(true);
    setEditingRecord(record);

    // Set initial form data without dates first
    setFormData({
      acType: record.AC_Type || '0',
      fromDate: '0',
      toDate: '0',
      approvalDate: record.Approval_Date || '',
    });

    setExistingWordFile(record.MOMfile || '');
    setExistingPdfFile(record.MOM_Format || '');
    
    // Set initial file data for FileUploadComponent
    if (record.MOMfile) {
      setUploadedWordFile(createFileObject(record.MOMfile));
    } else {
      setUploadedWordFile(null);
    }
    
    if (record.MOM_Format) {
      setUploadedPdfFile(createFileObject(record.MOM_Format));
    } else {
      setUploadedPdfFile(null);
    }
    
    // Increment key to force FileUploadComponent remount with initial files
    setFileUploadKey(prev => prev + 1);

    // Load dates for the AC Type and wait for it to complete
    if (record.AC_Type && record.AC_Type !== '0') {
      try {
        setLoading(true);
        
        // Load AC From dates
        const response = await acProcessService.getRecentACDates(record.AC_Type);
        const dates = Array.isArray(response) ? response : (response.data || []);
        setAcDates(dates);

        console.log('Available dates:', dates); // Debug log
        
        let fromDateId = '0';
        let toDateId = '0';

        // Find the from date ID - try multiple field names
        const fromDateValue = record.AC_Date || record.Ac_Date || record.acDate;
        if (fromDateValue) {
          // If it's already an ID/number
          const foundFromDate = dates.find(d => d.Id === fromDateValue || d.Id.toString() === fromDateValue.toString());
          if (foundFromDate) {
            fromDateId = foundFromDate.Id.toString();
          }
        } else if (record.Date) {
          // Try to match by date string
          const foundFromDate = dates.find(d => d.Date === record.Date);
          if (foundFromDate) {
            fromDateId = foundFromDate.Id.toString();
          }
        }

        console.log('Found fromDateId:', fromDateId); // Debug log

        // If we have a fromDate, load the toDate options
        if (fromDateId !== '0') {
          const selectedDate = dates.find(d => d.Id.toString() === fromDateId);
          if (selectedDate) {
            const responseTo = await acProcessService.getACDateToByDate(
              record.AC_Type,
              selectedDate.Date
            );
            const datesTo = Array.isArray(responseTo) ? responseTo : (responseTo.data || []);
            setAcDatesTo(datesTo);

            console.log('Available to dates:', datesTo); // Debug log

            // Find the to date ID - try multiple field names
            const toDateValue = record.Ac_Date_To || record.acDateTo || record.AcDateTo;
            if (toDateValue) {
              // If it's already an ID/number
              const foundToDate = datesTo.find(d => (d.Id || d.id) === toDateValue || (d.Id || d.id)?.toString() === toDateValue.toString());
              if (foundToDate) {
                toDateId = (foundToDate.Id || foundToDate.id).toString();
              }
            } else if (record.DateTo) {
              // Try to match by date string
              const foundToDate = datesTo.find(d => d.DateTo === record.DateTo);
              if (foundToDate) {
                toDateId = (foundToDate.Id || foundToDate.id).toString();
              }
            }

            console.log('Found toDateId:', toDateId); // Debug log
          }
        }

        // Now set the form data with the date IDs after options are loaded
        setFormData(prev => ({
          ...prev,
          fromDate: fromDateId,
          toDate: toDateId,
        }));
      } catch (err) {
        setError('Failed to load AC dates for editing');
        console.error('Edit error:', err);
      } finally {
        setLoading(false);
      }
    }
  };

  const createFileObject = (fileName) => {
    if (!fileName) return null;
    return {
      fileName: fileName,
      key: `live/${fileName}`,
      originalName: fileName,
    };
  };

  const handleFileOpen = async (fileUrl) => {
    if (!fileUrl) {
      setError('No file available to view');
      return;
    }

    try {
      setLoading(true);
      // Use s3Utils to handle file opening with signed URLs
      await s3Utils.handleFileViewWithS3(fileUrl, {
        showError: (msg) => setError(msg),
        showLoading: () => setLoading(true),
        hideLoading: () => setLoading(false),
        expiresIn: 172800 // 48 hours
      });
    } catch (err) {
      setError('Failed to open file');
      console.error('Error opening file:', err);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      acType: '0',
      fromDate: '0',
      toDate: '0',
      approvalDate: '',
    });
    setExistingWordFile('');
    setExistingPdfFile('');
    setUploadedWordFile(null);
    setUploadedPdfFile(null);
    setIsEditMode(false);
    setEditingRecord(null);
    setAcDates([]);
    setAcDatesTo([]);
    // Increment key to force FileUploadComponent remount
    setFileUploadKey(prev => prev + 1);
  };

  const acTypeOptions = [
    { value: '0', label: '---Select AC Type---' },
    { value: 'SEC', label: 'SEC' },
    { value: 'Core', label: 'Core' },
    { value: 'CAT', label: 'CAT' },
    { value: 'CAM', label: 'CAM' },
    { value: 'CAC', label: 'CAC' },
    { value: 'SAC', label: 'SAC' },
    { value: 'CAT (Integrated)', label: 'CAT (Integrated)' },
    { value: 'PTP', label: 'PTP' },
    { value: 'RMP', label: 'RMP' },
    { value: 'Review Committee', label: 'Review Committee' },
  ];

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      {/* Header Section */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          borderRadius: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
              Upload MOM
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.9 }}>
              Upload Minutes of Meeting for AC Process
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/main/convener/dashboard')}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.3)' },
            }}
          >
            Back to Dashboard
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Upload Form */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardContent sx={{ p: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                <CloudUploadIcon sx={{ fontSize: 40, color: 'primary.main' }} />
                <Typography variant="h5" sx={{ fontWeight: 600 }}>
                  {isEditMode ? 'Update MOM' : 'Upload MOM'}
                </Typography>
              </Box>

              <Grid container spacing={3} sx={{ mt: 2 }}>
                {/* AC Type */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Select AC Type</InputLabel>
                    <Select
                      value={formData.acType}
                      label="Select AC Type"
                      onChange={(e) => handleACTypeChange(e.target.value)}
                    >
                      {acTypeOptions.map(option => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* From Date */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Select From Date</InputLabel>
                    <Select
                      value={formData.fromDate}
                      label="Select From Date"
                      onChange={(e) => handleFromDateChange(e.target.value)}
                      disabled={formData.acType === '0'}
                    >
                      <MenuItem value="0">-- Select Date --</MenuItem>
                      {acDates.map(date => (
                        <MenuItem key={date.Id} value={date.Id}>
                          {date.Date}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* To Date */}
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Select To Date</InputLabel>
                    <Select
                      value={formData.toDate}
                      label="Select To Date"
                      onChange={(e) => setFormData(prev => ({ ...prev, toDate: e.target.value }))}
                      disabled={formData.fromDate === '0'}
                    >
                      <MenuItem value="0">-- Select Date --</MenuItem>
                      {acDatesTo.map(date => (
                        <MenuItem key={date.Id || date.id} value={date.Id || date.id}>
                          {date.DateTo}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Approval Date */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Select Approval Date"
                    type="date"
                    required
                    value={formData.approvalDate}
                    onChange={(e) => setFormData(prev => ({ ...prev, approvalDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{
                      max: new Date().toISOString().split('T')[0],
                    }}
                  />
                </Grid>

                {/* Word File Upload */}
                <Grid item xs={12} md={6}>
                  <FileUploadComponent
                    key={`wordFile-${fileUploadKey}`}
                    fieldName="wordFile"
                    label="Upload MOM File in Word"
                    orgId="convener"
                    section="mom-documents"
                    accept=".doc,.docx,.DOC,.DOCX"
                    maxSize={10}
                    onChange={handleFileUploadChange}
                    initialFile={uploadedWordFile}
                    localDeleteOnly={false}
                    required
                  />
                </Grid>

                {/* PDF File Upload */}
                <Grid item xs={12} md={6}>
                  <FileUploadComponent
                    key={`pdfFile-${fileUploadKey}`}
                    fieldName="pdfFile"
                    label="Upload MOM File in PDF"
                    orgId="convener"
                    section="mom-documents"
                    accept=".pdf,.PDF"
                    maxSize={10}
                    onChange={handleFileUploadChange}
                    initialFile={uploadedPdfFile}
                    localDeleteOnly={false}
                    required
                  />
                </Grid>

                {/* Submit Button */}
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
                    <Button
                      variant="contained"
                      onClick={handleSubmitClick}
                      disabled={loading}
                    >
                      {loading ? <CircularProgress size={24} /> : isEditMode ? 'Update' : 'Submit'}
                    </Button>
                    {isEditMode && (
                      <Button
                        variant="outlined"
                        onClick={resetForm}
                      >
                        Cancel
                      </Button>
                    )}
                  </Box>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        {/* MOM Records Table */}
        <Grid item xs={12}>
          <Card elevation={3} sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
                Uploaded MOM Records
              </Typography>
              <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                  <TableCell><strong>Sr. No.</strong></TableCell>
                  <TableCell><strong>Edit</strong></TableCell>
                  <TableCell><strong>AC Type</strong></TableCell>
                  <TableCell><strong>From Date</strong></TableCell>
                  <TableCell><strong>To Date</strong></TableCell>
                  <TableCell><strong>Approval Date</strong></TableCell>
                  <TableCell><strong>MOM File (Word)</strong></TableCell>
                  <TableCell><strong>MOM File (PDF)</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {momRecords.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No records found
                    </TableCell>
                  </TableRow>
                ) : (
                  momRecords.map((record, index) => (
                    <TableRow key={index} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleEdit(record)}
                        >
                          <EditIcon />
                        </IconButton>
                      </TableCell>
                      <TableCell>{record.AC_Type}</TableCell>
                      <TableCell>{record.Date}</TableCell>
                      <TableCell>{record.DateTo}</TableCell>
                      <TableCell>{record.Approval_Date ? new Date(record.Approval_Date).toLocaleDateString() : '-'}</TableCell>
                      <TableCell>
                        {record.MOMfile && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleFileOpen(record.MOMfile)}
                            disabled={loading}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        )}
                      </TableCell>
                      <TableCell>
                        {record.MOM_Format && (
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleFileOpen(record.MOM_Format)}
                            disabled={loading}
                          >
                            <VisibilityIcon />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Password Verification Dialog */}
      <Dialog open={passwordDialog} onClose={() => setPasswordDialog(false)}>
        <DialogTitle>
          Send application to Dealing Officer for AC Process
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom sx={{ mt: 2 }}>
            Enter Your Password to Continue
          </Typography>
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            sx={{ mt: 2 }}
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPasswordDialog(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default UploadMOM;
